import {
  BackofficeGetPromoCounterAdminQuery,
  BackofficePromoCounter,
} from '@diamond/shared/types';
import { apiInstance } from '@diamond/shared/utils';
import { useAuthStore } from '@diamond/sol-admin/features/auth';
import { useQuery } from '@tanstack/react-query';

/**
 * Fetches promotion counter statistics from the backoffice API.
 *
 * @param token - The authentication token for the API request.
 * @param filters - The filters for the API request.
 * @returns A promise that resolves to the promotion counter data.
 * @throws Throws an error if the API request fails.
 */
export const getPromoCounter = async (
  token: string,
  filters?: BackofficeGetPromoCounterAdminQuery
): Promise<BackofficePromoCounter> => {
  const apiUrl = 'backoffice/promo/counter';
  try {
    const { data } = await apiInstance(token).get<BackofficePromoCounter>(
      apiUrl,
      {
        params: filters,
      }
    );
    return data;
  } catch (error) {
    console.error('Error fetching promo counter via apiInstance:', error);
    throw error;
  }
};

export const BACKOFFICE_PROMO_COUNTER_QUERY_KEY = 'BackofficePromoCounter';

/**
 * React Query hook for fetching and managing promotion counter data.
 *
 * @param filters - The filters for the API request.
 * @returns An object containing the query state, data, and fetch status.
 */
export function usePromoCounter(filters?: BackofficeGetPromoCounterAdminQuery) {
  const { access_token } = useAuthStore();
  return useQuery({
    queryKey: [BACKOFFICE_PROMO_COUNTER_QUERY_KEY, filters],
    queryFn: () => getPromoCounter(access_token, filters),
    gcTime: 0,
    staleTime: 5 * 60 * 1000, // Cache data for 5 minutes
    retry: 3, // Retry failed requests up to 3 times
  });
}
